/**
 * components/UraReferencePicker.js
 * ---------------------------------------------------------------------------
 * The ONE searchable picker for URA reference codes, in two flavours:
 *
 *   <UraLeafPicker />   a URA commodity code (UNSPSC leaf: code, name, rate)
 *   <UraUnitPicker />   a URA measure unit (code, name)
 *
 * Used inline by the category and unit tables and by their edit forms, so a
 * mapping is assigned the same way everywhere. Built on the react-select
 * AsyncSelect the rest of the dashboard already uses: search as you type
 * (debounced, server-side; the commodity list is about 148,000 leaves and is
 * never fetched whole), arrow keys and Enter to choose, a clear (x) action,
 * and a trailing "n more matches, refine your search" row when the server had
 * more than it returned.
 *
 * When the server reports `present: false` the dictionaries are not loaded
 * there yet: the picker says so in plain words and falls back to a manual
 * code field, which is also reachable on demand ("Type a code instead").
 *
 * A mapped code is a reference lookup, not a filed document. The copy here
 * says "URA commodity code" and "URA unit" and nothing about compliance.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AppAsyncSelect as AsyncSelect } from "./ui/AppReactSelect";
import {
  fetchUraCommodityPresets,
  fetchUraUnitPresets,
} from "../api/index.js";
import {
  URA_MIN_QUERY_LENGTH,
  URA_NOT_LOADED_TEXT,
  formatUraRate,
  normaliseUraLeaf,
  normaliseUraPresetResponse,
  normaliseUraUnit,
} from "../utilities/uraMapping";
import "./UraReferencePicker.css";

const DEBOUNCE_MS = 300;
const PAGE_SIZE = 50;
const MORE_ROW = "__ura_more_matches__";

const errorText = (err, fallback) =>
  err?.response?.data?.message || err?.message || fallback;

const includesQuery = (item, needle) =>
  String(item?.code || "").toLowerCase().includes(needle) ||
  String(item?.name || "").toLowerCase().includes(needle);

/* Everything that differs between the two flavours lives here, so the
   component body below is written once. */
const KINDS = {
  commodity: {
    label: "URA commodity code",
    placeholder: "Search URA commodity codes by name or code",
    normalise: normaliseUraLeaf,
    search: async (q) => {
      const { data } = await fetchUraCommodityPresets(q, PAGE_SIZE);
      return normaliseUraPresetResponse(data);
    },
  },
  unit: {
    label: "URA unit",
    placeholder: "Search URA units by name or code",
    normalise: normaliseUraUnit,
    search: async (q) => {
      const { data } = await fetchUraUnitPresets(q);
      const envelope = normaliseUraPresetResponse(data);
      /* An earlier handler ignores `q` and returns the whole unit list (about
         535 rows). Recognisable because it hands back more than a page; the
         search then happens here so the picker behaves the same either way. */
      if (q && envelope.items.length > PAGE_SIZE) {
        const needle = q.toLowerCase();
        const matches = envelope.items
          .map(normaliseUraUnit)
          .filter((item) => item && includesQuery(item, needle));
        return { ...envelope, items: matches, total: matches.length };
      }
      return envelope;
    },
  },
};

const toOption = (item) =>
  item ? { value: item.code, label: [item.code, item.name].filter(Boolean).join(" "), data: item } : null;

const moreRow = (hidden) => ({
  value: MORE_ROW,
  isDisabled: true,
  label: `${hidden} more ${hidden === 1 ? "match" : "matches"}, refine your search`,
});

const formatOptionLabel = (option, { context }) => {
  if (option.value === MORE_ROW) {
    return <span className="ura-picker__more">{option.label}</span>;
  }
  const item = option.data || {};
  const rate = formatUraRate(item.rate);
  return (
    <span className={`ura-picker__option ura-picker__option--${context}`}>
      <span className="ura-picker__code">{item.code}</span>
      <span className="ura-picker__name">{item.name || "Unnamed"}</span>
      {rate && context === "menu" ? (
        <span className="ura-picker__rate">{rate}</span>
      ) : null}
      {item.isService && context === "menu" ? (
        <span className="ura-picker__tag">Service</span>
      ) : null}
    </span>
  );
};

const portalStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 1500 }),
  menu: (base) => ({ ...base, minWidth: 260 }),
};

export const UraReferencePicker = ({
  kind,
  value,
  onChange,
  onCancel,
  isDisabled = false,
  autoFocus = false,
  inputId,
  placeholder,
  compact = false,
  className = "",
}) => {
  const cfg = KINDS[kind] || KINDS.commodity;

  /* null until the server has answered once; then true or false. */
  const [present, setPresent] = useState(null);
  const [error, setError] = useState("");
  const [manual, setManual] = useState(false);
  const [manualCode, setManualCode] = useState(value?.code || "");

  const mounted = useRef(true);
  const timer = useRef(null);
  const probed = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  /* One cheap request on first focus, so "not loaded" is said before the
     admin has typed anything rather than after a search finds nothing. */
  const probe = useCallback(async () => {
    if (probed.current) return;
    probed.current = true;
    try {
      const envelope = await cfg.search("");
      if (mounted.current) setPresent(envelope.present);
    } catch (err) {
      if (mounted.current) setError(errorText(err, "Could not reach the URA reference list."));
    }
  }, [cfg]);

  useEffect(() => {
    if (present === false) setManual(true);
  }, [present]);

  const loadOptions = useCallback(
    (input) =>
      new Promise((resolve) => {
        const q = String(input || "").trim();
        if (!q) {
          resolve([]);
          return;
        }
        if (timer.current) clearTimeout(timer.current);
        /* Only the latest keystroke's request is sent; earlier promises stay
           pending and react-select ignores anything but its last request. */
        timer.current = setTimeout(async () => {
          try {
            const envelope = await cfg.search(q);
            if (!mounted.current) return;
            setPresent(envelope.present);
            setError("");
            const options = envelope.items.map(cfg.normalise).filter(Boolean).map(toOption);
            const hidden = envelope.total - envelope.items.length;
            if (hidden > 0) options.push(moreRow(hidden));
            resolve(options);
          } catch (err) {
            if (mounted.current) setError(errorText(err, "Search failed."));
            resolve([]);
          }
        }, DEBOUNCE_MS);
      }),
    [cfg]
  );

  const selected = useMemo(() => toOption(value ? cfg.normalise(value) : null), [value, cfg]);

  const noOptionsMessage = ({ inputValue }) => {
    const q = String(inputValue || "").trim();
    if (present === false) return URA_NOT_LOADED_TEXT;
    if (q.length < URA_MIN_QUERY_LENGTH) {
      return `Type at least ${URA_MIN_QUERY_LENGTH} characters, or an exact code`;
    }
    return "No URA codes match that search";
  };

  const applyManual = () => {
    const code = manualCode.trim();
    onChange(code ? cfg.normalise({ code, name: null }) : null);
  };

  const controlLabel = cfg.label;

  return (
    <div
      className={`ura-picker ${compact ? "ura-picker--compact" : ""} ${className}`.trim()}
    >
      <div className="ura-picker__row">
        {manual ? (
          <div className="ura-picker__manual">
            <input
              id={inputId}
              type="text"
              className="ura-picker__manual-input"
              value={manualCode}
              placeholder={`${controlLabel}, typed exactly as URA lists it`}
              aria-label={`${controlLabel}, manual entry`}
              disabled={isDisabled}
              autoFocus={autoFocus}
              onChange={(event) => setManualCode(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  applyManual();
                }
              }}
            />
            <button
              type="button"
              className="ura-picker__button ura-picker__button--primary"
              onClick={applyManual}
              disabled={isDisabled}
            >
              Apply
            </button>
          </div>
        ) : (
          <AsyncSelect
            inputId={inputId}
            className="ura-picker__select"
            classNamePrefix="ura-picker"
            value={selected}
            loadOptions={loadOptions}
            defaultOptions={false}
            cacheOptions={false}
            filterOption={null}
            isClearable
            isDisabled={isDisabled}
            autoFocus={autoFocus}
            openMenuOnFocus={autoFocus}
            onFocus={probe}
            onMenuOpen={probe}
            onChange={(option) => onChange(option && option.value !== MORE_ROW ? option.data : null)}
            isOptionDisabled={(option) => option.value === MORE_ROW}
            formatOptionLabel={formatOptionLabel}
            noOptionsMessage={noOptionsMessage}
            loadingMessage={() => "Searching URA reference list"}
            placeholder={placeholder || cfg.placeholder}
            aria-label={controlLabel}
            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            menuPosition="fixed"
            styles={portalStyles}
          />
        )}
        {onCancel ? (
          <button
            type="button"
            className="ura-picker__button"
            onClick={onCancel}
            disabled={isDisabled}
          >
            Cancel
          </button>
        ) : null}
      </div>

      <div className="ura-picker__foot">
        {present === false ? (
          <span className="ura-picker__notice">{URA_NOT_LOADED_TEXT}</span>
        ) : null}
        {error ? <span className="ura-picker__error">{error}</span> : null}
        {present !== false ? (
          <button
            type="button"
            className="ura-picker__link"
            onClick={() => {
              setManualCode(value?.code || "");
              setManual((current) => !current);
            }}
            disabled={isDisabled}
          >
            {manual ? "Back to search" : "Type a code instead"}
          </button>
        ) : null}
      </div>
    </div>
  );
};

export const UraLeafPicker = (props) => <UraReferencePicker {...props} kind="commodity" />;
export const UraUnitPicker = (props) => <UraReferencePicker {...props} kind="unit" />;

export default UraReferencePicker;
